import { Attributes, NeoModel } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { NotificationDuration } from 'st-app/Models/Enums/NotificationDuration';
import { PreferredCommunicationMethod } from '../Models/Enums/PreferredCommunicationMethod';
import UpdateCommunicationPreferenceCommand from '../Models/Commands/UpdateCommunicationPreferenceCommand';
import ParticipantCommunicationPreferenceLookup from '../Models/Lookups/ParticipantCommunicationPreferenceLookup';
import { AppService, Types } from '../ParticipantsTypes';

@NeoModel
export default class PreferredCommunicationMethodVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public apiClient = AppService.get(Types.Participants.ApiClients.MainApiClient)) {

        super(taskRunner);
    }

    public preference = new ParticipantCommunicationPreferenceLookup();

    /** The method selected in the UI. Null means "Use Org Default". */
    @Attributes.Nullable()
    public selectedMethod: PreferredCommunicationMethod | null = null;

    public async initialise() {
        const result = await this.taskRunner.waitFor(this.apiClient.getCommunicationPreference());
        this.preference = ParticipantCommunicationPreferenceLookup.fromJSObject<ParticipantCommunicationPreferenceLookup>(result.data);
        this.selectedMethod = this.preference.preferredCommunicationMethod;

        // SMS requires a verified phone number. Clear any saved SMS selection so the
        // UI selection stays consistent with what the user is actually allowed to pick.
        if (this.isSmsBlocked && this.selectedMethod === PreferredCommunicationMethod.Sms) {
            this.selectedMethod = null;
        }
    }

    /** True when SMS is unavailable because the participant's mobile number is not verified. */
    public get isSmsBlocked(): boolean {
        return !this.preference.canSetSmsPreference;
    }

    /** The effective method that will be applied after saving with the current selection. */
    public get effectiveMethodPreview(): PreferredCommunicationMethod | null {
        return this.selectedMethod ?? this.preference.tenantDefaultCommunicationMethod;
    }

    public savePreference() {
        if (this.isSmsBlocked && this.selectedMethod === PreferredCommunicationMethod.Sms) {
            this.notifications.addWarning("Cannot save", "SMS requires a verified phone number. Please verify your mobile number in Contact Details first.", NotificationDuration.Standard);
            return;
        }

        this.taskRunner.run(async () => {
            const command = new UpdateCommunicationPreferenceCommand();
            command.preferredCommunicationMethod = this.selectedMethod;

            await this.apiClient.updateCommunicationPreference(command.toJSObject());

            this.preference.preferredCommunicationMethod = this.selectedMethod;
            this.notifications.addSuccess("Preference saved", "Your communication preference has been updated.", NotificationDuration.Standard);
        });
    }

    public get effectiveMethodDisplay(): string {
        switch (this.effectiveMethodPreview) {
            case PreferredCommunicationMethod.Email: return "Email";
            case PreferredCommunicationMethod.Sms: return "SMS";
            default: return "Not set";
        }
    }
}
